import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter v34 — Karyawan Step 1 Registration (simplified).
 * Hanya minta nama + email + password.
 *
 * Flow lengkap:
 *   1. Daftar (halaman ini)   → status `registration_pending`
 *   2. Verifikasi email       → /verify-email?token=...
 *   3. Login                  → /login
 *   4. Lengkapi profil + KTP + bank + wajah → /karyawan/profil
 *   5. Tunggu persetujuan HR  → status `pending_verification` → `active`
 */
export default function KaryawanRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) return toast.error("Nama wajib");
    if (!form.email.trim()) return toast.error("Email wajib");
    if (form.password.length < 6) return toast.error("Password minimal 6 karakter");
    if (form.password !== form.confirmPassword) return toast.error("Konfirmasi password tidak cocok");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password };
      const r = await axios.post(`${API}/auth/register-karyawan`, payload);
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pendaftaran gagal");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#f6f5f0] flex items-center justify-center p-6">
        <div className="bg-white border border-stone-200/70 rounded-2xl max-w-lg w-full p-8 space-y-4" data-testid="karyawan-register-success">
          <CheckCircle2 className="h-12 w-12 text-emerald-600" />
          <h2 className="font-display text-2xl font-bold text-stone-900">Pendaftaran Berhasil</h2>
          <p className="text-sm text-stone-700">{done.message || "Akun Anda telah dibuat. Cek email untuk verifikasi."}</p>
          <div className="bg-stone-50 border border-stone-200 p-3 text-xs space-y-1 font-mono">
            <div><span className="text-stone-500">Email:</span> {done.email}</div>
            <div><span className="text-stone-500">Kode Karyawan:</span> {done.employee_code}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 space-y-2">
            <div className="font-semibold flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Langkah Selanjutnya:</div>
            <ol className="list-decimal list-inside space-y-0.5 ml-1">
              <li>Buka email Anda & klik link verifikasi (berlaku 24 jam)</li>
              <li>Login dengan email & password yang baru Anda buat</li>
              <li>Lengkapi data diri (KTP, Bank, Wajah) di halaman profil</li>
              <li>Tunggu HR menyetujui sebelum bisa absen</li>
            </ol>
          </div>
          <button onClick={() => nav("/login")} className="w-full bg-stone-900 hover:bg-blue-700 text-white py-3 rounded-full text-sm font-medium" data-testid="register-go-login">Kembali ke Login</button>
        </div>
      </div>
    );
  }

  const inputCls = "w-full border border-stone-300 bg-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const lbl = "text-[11px] uppercase tracking-widest text-stone-600 font-medium mb-1 flex items-center gap-1.5";

  return (
    <div className="min-h-screen bg-[#f6f5f0] py-8">
      <div className="max-w-md mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-terra-700 font-medium">Pendaftaran Karyawan · Step 1 / 5</p>
            <h1 className="font-display text-3xl font-bold text-stone-900 mt-1">Buat Akun</h1>
            <p className="text-sm text-stone-600 mt-1">Cukup nama, email & password dulu. Data lengkap diisi setelah Anda login.</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-stone-200 p-6 space-y-5" data-testid="karyawan-register-form">
          <div>
            <label className={lbl}><User className="h-3 w-3" /> Nama Lengkap *</label>
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="reg-name" />
          </div>
          <div>
            <label className={lbl}><Mail className="h-3 w-3" /> Email *</label>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" />
            <p className="text-[11px] text-stone-500 mt-1">Email akan menjadi username login. Pastikan dapat menerima link verifikasi.</p>
          </div>
          <div>
            <label className={lbl}><Lock className="h-3 w-3" /> Password (min 6 karakter) *</label>
            <input type="password" className={inputCls} value={form.password} onChange={(e) => set("password", e.target.value)} minLength={6} required data-testid="reg-password" />
          </div>
          <div>
            <label className={lbl}><Lock className="h-3 w-3" /> Konfirmasi Password *</label>
            <input type="password" className={inputCls} value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)} minLength={6} required data-testid="reg-password2" />
          </div>

          <div className="bg-stone-50 border border-stone-200 p-3 text-[11px] text-stone-600">
            <b>Alur Pendaftaran:</b> Buat Akun → Verifikasi Email → Login → Lengkapi Profil (KTP, Bank, Wajah) → Disetujui HR → Mulai Absen.
          </div>

          <button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-blue-700 text-white py-3 rounded-full text-sm font-medium tracking-wide disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="reg-submit">
            {busy ? "Memproses…" : <>Daftar & Kirim Verifikasi <ArrowRight className="h-4 w-4" /></>}
          </button>

          <div className="text-center pt-2 border-t border-stone-100">
            <Link to="/login" className="text-xs text-blue-700 hover:underline">Sudah punya akun? Login →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
